/** @jsx jsx */
import { PropsWithAs } from "@eworkit/iris";
import { AnimatePresence, motion } from "framer-motion";
import React, { PropsWithChildren, ReactElement } from "react";
import { Dialog as ReakitDialog, DialogDisclosure, useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { DialogContainer } from "../DialogContainer";
import {
  fullScreenDropdownAnimationStyles,
  fullScreenDropdownContainerStyles,
  fullScreenDropdownDialogStyles,
} from "./../config";

export interface FullScreenDropdownProps extends React.ComponentPropsWithoutRef<typeof ReakitDialog> {
  disclosureContent?: ReactElement;
  openDisclosureProps?: any;
  closedDisclosureProps?: any;
}

export const FullScreenDropdown = React.forwardRef<
  HTMLDivElement,
  PropsWithAs<PropsWithChildren<FullScreenDropdownProps>>
>((props, ref) => {
  const {
    children,
    hideOnClickOutside,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    hideOnEsc,
    as,
    disclosureContent,
    openDisclosureProps,
    closedDisclosureProps,
  } = props;
  const fullScreenDropdownState = useDialogState({ animated: true });

  return (
    <React.Fragment>
      <DialogDisclosure
        as={as}
        {...(fullScreenDropdownState.visible ? openDisclosureProps : closedDisclosureProps)}
        {...fullScreenDropdownState}>
        {disclosureContent}
      </DialogDisclosure>

      <ReakitDialog
        data-component="full-screen-dropdown"
        hideOnClickOutside={fullScreenDropdownState.animating ? false : hideOnClickOutside}
        hideOnEsc={hideOnEsc}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        ref={ref}
        sx={fullScreenDropdownDialogStyles}
        {...fullScreenDropdownState}>
        <AnimatePresence>
          {fullScreenDropdownState.visible && (
            <motion.div
              key="full-screen-dropdown"
              initial={{
                y: -80,
                opacity: 0,
              }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -80 }}
              transition={{ duration: 0.3 }}
              sx={fullScreenDropdownAnimationStyles}>
              <DialogContainer sx={fullScreenDropdownContainerStyles}>{children}</DialogContainer>
            </motion.div>
          )}
        </AnimatePresence>
      </ReakitDialog>
    </React.Fragment>
  );
});
